/**
 * The MyCorr clipboard payload: TSV for the world, plus the `MycorrCopyMeta`
 * that tells paste a column or view was copied rather than plain text.
 *
 * Two browser constraints shape this module.
 *
 * The metadata rides a `web ` custom clipboard format, which only Chromium
 * implements — Safari's `ClipboardItem` accepts `text/plain`, `text/html`,
 * `text/uri-list` and `image/png`, and rejects the entire write otherwise. The
 * metadata names view and column ids belonging to the *currently open model*,
 * so it is only ever meaningful in the tab that produced it; an in-tab memo is
 * the honest scope for it, and the custom format is kept only as a bonus that
 * lets Chromium carry a copy across tabs of the same model.
 *
 * `navigator.clipboard.write()` is rejected outright once the user gesture has
 * lapsed. Copies that fetch their rows from the server therefore cannot wait
 * for the data and write afterwards — they must issue the write during the
 * keypress and hand `ClipboardItem` a promise, which is what the async
 * Clipboard API provides promise-valued entries for.
 */

export const MYCORR_CLIPBOARD_TYPE = "web application/mycorr";

/** The last payload this tab put on the clipboard, keyed by its text. */
let lastCopy: { text: string; meta: string } | null = null;

/**
 * `ClipboardItem.supports` is the standard probe for optional flavors. A
 * browser too old to expose it never shipped custom formats either, so
 * treating it as absent is correct rather than merely safe.
 */
function supportsMycorrType(): boolean {
  return (
    typeof ClipboardItem.supports === "function" &&
    ClipboardItem.supports(MYCORR_CLIPBOARD_TYPE)
  );
}

function blobOf(value: string | Promise<string>, type: string): Promise<Blob> {
  return Promise.resolve(value).then((text) => new Blob([text], { type }));
}

/**
 * Put a copy on the clipboard. `text` may be a promise — the write is issued
 * before it settles, so a copy whose rows are still loading stays inside the
 * user gesture that triggered it. MUST be called synchronously from the
 * originating event handler.
 */
export async function writeClipboard(
  text: string | Promise<string>,
  meta?: string | null,
): Promise<void> {
  const item: Record<string, Promise<Blob>> = {
    "text/plain": blobOf(text, "text/plain"),
  };
  if (meta && supportsMycorrType()) {
    item[MYCORR_CLIPBOARD_TYPE] = blobOf(meta, MYCORR_CLIPBOARD_TYPE);
  }
  await navigator.clipboard.write([new ClipboardItem(item)]);
  // The memo mirrors what is actually on the clipboard now, so a metadata-less
  // copy has to clear it rather than leave the previous one to be recalled.
  lastCopy = meta ? { text: await Promise.resolve(text), meta } : null;
}

/**
 * The `MycorrCopyMeta` for a pasted clipboard item, or `null` when the paste
 * didn't come from this tab (or came from another app entirely).
 */
export async function readClipboardMeta(
  item: ClipboardItem,
  text: string,
): Promise<string | null> {
  if (item.types.includes(MYCORR_CLIPBOARD_TYPE)) {
    return await (await item.getType(MYCORR_CLIPBOARD_TYPE)).text();
  }
  return lastCopy?.text === text ? lastCopy.meta : null;
}

/** Test seam — drops the remembered copy. */
export function forgetClipboardMeta(): void {
  lastCopy = null;
}
